"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import Image from "next/image";

const defaultSteps = [
  {
    number: 1,
    title: "Boka din flytt",
    description:
      "Ungdomskraft flyttar lägenheter, hus och kontor lokalt. Ange dina adresser, välj fordon och team, och boka en tid som passar dig.",
  },
  {
    number: 2,
    title: "Vi hämtar och forslar bort allt",
    description:
      "Våra ungdomar kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus och trånga hissar – du behöver inte lyfta ett finger.",
  },
  {
    number: 3,
    title: "Allt bortforslat",
    description:
      "Vi kör allt till närmsta avfallsstation eller återvinningscentral. Du slipper tänka på sortering och transport.",
  },
];

interface HowItWorksProps {
  heading?: string;
  steps?: { number: number; title: string; description: string }[];
}

export default function HowItWorks({
  heading = "Boka din flytt i 3 enkla steg",
  steps = defaultSteps,
}: HowItWorksProps = {}) {
  const sectionRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const mobilePathRef = useRef<SVGPathElement>(null);
  const vanRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [pathD, setPathD] = useState("");
  const [mobilePathD, setMobilePathD] = useState("");
  const [pathLength, setPathLength] = useState(0);
  const sampledPoints = useRef<{ x: number; y: number; angle: number }[]>([]);

  const buildPath = useCallback(() => {
    const container = containerRef.current;
    const cards = cardRefs.current;
    if (!container || cards.some((c) => !c)) return;

    const cr = container.getBoundingClientRect();

    const pos = cards.map((card) => {
      const r = card!.getBoundingClientRect();
      return {
        l: r.left - cr.left,
        t: r.top - cr.top,
        r: r.right - cr.left,
        b: r.bottom - cr.top,
      };
    });

    const isDesktop = window.innerWidth >= 1024;

    if (isDesktop) {
      const [c1, c2, c3] = pos;
      const R = 14; // corner rounding
      const P = 8;  // padding outside cards

      const topY = c1.t - P;
      const botY = c1.b + P;
      const gapX1 = (c1.r + c2.l) / 2;
      const gapX2 = (c2.r + c3.l) / 2;

      const d = [
        `M ${c1.l + (c1.r - c1.l) * 0.1},${topY}`,
        `L ${gapX1 - R},${topY}`,
        `Q ${gapX1},${topY} ${gapX1},${topY + R}`,
        `L ${gapX1},${botY - R}`,
        `Q ${gapX1},${botY} ${gapX1 + R},${botY}`,
        `L ${gapX2 - R},${botY}`,
        `Q ${gapX2},${botY} ${gapX2},${botY - R}`,
        `L ${gapX2},${topY + R}`,
        `Q ${gapX2},${topY} ${gapX2 + R},${topY}`,
        `L ${cr.width},${topY}`,
      ].join(" ");

      setPathD(d);
      setMobilePathD("");
    } else {
      // Mobile: L-shaped line from badge 1 → right → straight down to badge 3
      const [c1, , c3] = pos;
      const badgeSize = 48;
      const badgeTopOffset = 8;
      const R = 12;

      const badge1CenterY = c1.t + badgeTopOffset + badgeSize / 2;
      const startX = badgeSize + 32; // right edge of badge + spacing
      const lineX = cr.width - 16; // near right edge of container

      const d = [
        `M ${startX},${badge1CenterY}`,
        `L ${lineX - R},${badge1CenterY}`,
        `Q ${lineX},${badge1CenterY} ${lineX},${badge1CenterY + R}`,
        `L ${lineX},${c3.b - 24}`,
      ].join(" ");

      setMobilePathD(d);
      setPathD("");
    }
  }, []);

  useEffect(() => {
    const path = pathD ? pathRef.current : mobilePathD ? mobilePathRef.current : null;
    // Translation tools (Google Translate etc.) can swap the node for one
    // without SVG geometry methods — bail instead of crashing.
    if (!path || typeof path.getTotalLength !== "function") return;
    const len = path.getTotalLength();
    // Längden mäts på den ritade SVG-banan och finns inte förrän noden är i
    // DOM:en — den kan inte räknas fram under render.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPathLength(len);

    // Pre-compute ~200 sampled points so scroll handler avoids getPointAtLength()
    const SAMPLES = 200;
    const points: { x: number; y: number; angle: number }[] = [];
    for (let i = 0; i <= SAMPLES; i++) {
      const dist = (i / SAMPLES) * len;
      const pt = path.getPointAtLength(dist);
      const ahead = path.getPointAtLength(Math.min(dist + 5, len));
      const angle = Math.atan2(ahead.y - pt.y, ahead.x - pt.x) * (180 / Math.PI);
      points.push({ x: pt.x, y: pt.y, angle });
    }
    sampledPoints.current = points;
  }, [pathD, mobilePathD]);

  useEffect(() => {
    const timer = setTimeout(() => buildPath(), 50);
    const onResize = () => buildPath();
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", onResize);
    };
  }, [buildPath]);

  useEffect(() => {
    const section = sectionRef.current;
    const van = vanRef.current;
    const pts = sampledPoints.current;
    if (!section || !van || pts.length === 0) return;

    let rafId = 0;
    const isMobile = window.innerWidth < 1024;
    const offsetX = -40;
    const offsetY = isMobile ? -18 : -24;
    const maxIdx = pts.length - 1;
    const startFactor = isMobile ? 0.55 : 0.85;
    const speedFactor = isMobile ? 1.0 : 1.6;

    // Cache section position to avoid getBoundingClientRect every scroll frame
    let sectionTop = 0;
    let sectionHeight = 0;
    let vh = window.innerHeight;
    const cacheRect = () => {
      const rect = section.getBoundingClientRect();
      sectionTop = rect.top + window.scrollY;
      sectionHeight = rect.height;
      vh = window.innerHeight;
    };
    cacheRect();

    const tick = () => {
      const screenTop = sectionTop - window.scrollY;
      const rawProgress = (vh * startFactor - screenTop) / (sectionHeight * speedFactor);
      const progress = Math.min(Math.max(rawProgress, 0), 1);
      if (progress >= 1) return;

      // Interpolate between two pre-computed samples
      const fIdx = progress * maxIdx;
      const lo = Math.floor(fIdx);
      const hi = Math.min(lo + 1, maxIdx);
      const t = fIdx - lo;
      const x = pts[lo].x + (pts[hi].x - pts[lo].x) * t;
      const y = pts[lo].y + (pts[hi].y - pts[lo].y) * t;
      const angle = pts[lo].angle + (pts[hi].angle - pts[lo].angle) * t;

      van.style.opacity = "1";
      van.style.transform = `translate3d(${x + offsetX}px,${y + offsetY}px,0) rotate(${angle}deg)`;
    };

    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        tick();
      });
    };

    const handleResize = () => cacheRect();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });
    tick();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(rafId);
    };
  }, [pathLength]);

  return (
    <section ref={sectionRef} className="py-8 sm:pt-10 sm:pb-24 bg-white overflow-hidden">
      <div className="mx-auto max-w-screen-2xl px-6 xl:px-14">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark mb-16 sm:mb-20">
          {heading}
        </h2>

        <div ref={containerRef} className="relative">
          {/* One continuous road — always visible, van drives on it */}
          {pathD && (
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none hidden lg:block"
              style={{ overflow: "visible" }}
            >
              <path
                ref={pathRef}
                d={pathD}
                stroke="#f89544"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                vectorEffect="non-scaling-stroke"
                opacity="0.4"
              />
            </svg>
          )}

          {/* Mobile connecting line */}
          {mobilePathD && (
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none lg:hidden"
              style={{ overflow: "visible" }}
            >
              <path
                ref={mobilePathRef}
                d={mobilePathD}
                stroke="#f89544"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                opacity="0.4"
              />
            </svg>
          )}

          {/* Van */}
          <div
            ref={vanRef}
            className="absolute z-20 pointer-events-none"
            style={{ opacity: 0, willChange: "transform", top: 0, left: 0 }}
          >
            <Image
              src="/van-loaded-pin.svg"
              alt="Flyttbil"
              width={80}
              height={40}
              className="w-[80px] h-auto lg:w-[80px]"
            />
          </div>

          {/* Step cards */}
          <div className="grid grid-cols-1 auto-rows-fr lg:grid-cols-3 gap-2 lg:gap-6">
            {steps.map((step, i) => (
              <div
                key={step.number}
                ref={(el) => { cardRefs.current[i] = el; }}
                className={`relative py-8 sm:py-10 pt-18 sm:pt-20 pr-8 sm:pr-10 max-w-[85%] lg:max-w-none ${i > 0 ? "lg:pl-10" : ""}`}
              >
                <div className={`absolute top-2 left-0 ${i > 0 ? "lg:left-10" : ""} h-10 w-10 lg:h-12 lg:w-12 rounded-xl bg-brand-secondary flex items-center justify-center shadow-lg shadow-brand-secondary/30`}>
                  <span className="text-white text-lg lg:text-xl font-bold" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {step.number}
                  </span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-brand-dark mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
